import { STAFF_ROLES, type StaffRole } from "@puszczyk/contracts";
import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, unwrap } from "../../lib/api";
import { useApiMutation } from "../../lib/mutation";
import { Button } from "../../ui/Button";
import { Field } from "../../ui/Field";
import { Input } from "../../ui/Input";
import { Select } from "../../ui/Select";
import { toast } from "../../ui/toast";
import { MemberRow } from "./MemberRow";

/** Staff accounts of an organization (admin only). Public sign-up is disabled (docs/05). */
export function MembersPage({ organizationId }: { organizationId: string }) {
  const { t } = useTranslation();
  const members = useQuery({
    queryKey: ["members", organizationId],
    queryFn: () =>
      unwrap(api.organizations[":organizationId"].members.$get({ param: { organizationId } })),
  });
  const [form, setForm] = useState({
    email: "",
    name: "",
    role: "commander" as StaffRole,
    password: "",
  });
  const create = useApiMutation(
    () =>
      unwrap(
        api.organizations[":organizationId"].members.$post({
          param: { organizationId },
          json: form,
        }),
      ),
    {
      onSuccess: () => {
        toast(t("members.created"));
        setForm({ email: "", name: "", role: "commander", password: "" });
        void members.refetch();
      },
    },
  );
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.run();
  };

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4 p-6">
      <h1 className="text-lg font-semibold">{t("members.title")}</h1>
      <ul className="divide-y divide-zinc-800 rounded-lg border border-zinc-800 text-sm">
        {members.data?.map((m) => (
          <MemberRow
            key={m.userId}
            organizationId={organizationId}
            member={m}
            onSaved={() => void members.refetch()}
          />
        ))}
      </ul>
      <form
        onSubmit={submit}
        className="grid grid-cols-1 gap-3 rounded-lg border border-zinc-800 p-4 sm:grid-cols-2"
      >
        <h2 className="text-sm font-semibold sm:col-span-2">{t("members.add")}</h2>
        <Field label={t("auth.email")}>
          <Input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </Field>
        <Field label={t("members.name")}>
          <Input
            required
            minLength={2}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>
        <Field label={t("members.role")}>
          <Select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as StaffRole })}
          >
            {STAFF_ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`staffRole.${r}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("members.tempPassword")} hint={t("members.tempPasswordHint")}>
          <Input
            type="text"
            required
            minLength={10}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </Field>
        <Button
          type="submit"
          variant="primary"
          className="sm:col-span-2"
          disabled={create.isPending}
        >
          {t("members.add")}
        </Button>
      </form>
    </div>
  );
}
